import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { format } from 'date-fns';
import { get as _get } from 'lodash';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, mergeMap, take, takeUntil, withLatestFrom } from 'rxjs/operators';

// Store
import { AutoPaySelectors } from '@amfam/billing/auto-pay/data-access';
import {
  AutoPaySetupActions,
  AutoPaySteps,
  AutoPayUtilService
} from '@amfam/billing/auto-pay/feature';
import {
  BillAccountActions,
  BillAccountsSelectors,
  BillingNotificationsState
} from '@amfam/billing/billaccount/data-access';
import { OneTimePayActions } from '@amfam/billing/one-time-pay/feature';
import {
  fromPaymentHistoryActions,
  paymentHistoryQuery
} from '@amfam/billing/payment-history/data-access';
import {
  PaymentMethodActions,
  PaymentMethodSelectors
} from '@amfam/billing/payment-method/data-access';
import {
  PaymentMethod,
  PaymentMethodAccountType,
  PaymentMethodAddEditConfig,
  PaymentMethodAddEditOperationType
} from '@amfam/billing/payment-method/util';
import {
  pendingPaymentActions,
  pendingPaymentQuery
} from '@amfam/billing/pending-payment/data-access';
import { ScheduledPaymentSelectors } from '@amfam/billing/schedulepayment/data-access';
import { selectBillaccountsListNotEligibleForAutopay } from '@amfam/billing/shared/feature';
import { BillAccountTransmuteService } from '@amfam/billing/shared/util';
import {
  BillAccountActionButtons,
  BillAccountCard,
  BillAccountCardMetadata,
  BillAccountCardStatus,
  BillAccountStatusIcons,
  OverviewBillingUtilService
} from '@amfam/overview/billing/feature';
import { Policy } from '@amfam/policy/models';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import {
  AutomaticPayment,
  BillAccount,
  BillingAnalytics,
  BillingPaymentPaths,
  EntityNotification,
  ONLINE_BILLING,
  Payment,
  PaymentStatus,
  PolicyChangeRequestType,
  PolicyList,
  ScheduledPayment,
  SilentRegistrationPayload,
  UpdatePreferencesRequest
} from '@amfam/shared/models';
import { userQuery } from '@amfam/shared/user';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import {
  Applications,
  ApplicationService,
  CopyService,
  UtilService
} from '@amfam/shared/utility/shared-services';
import {
  Action,
  DockingBarService,
  DsModalService,
  LoadingSpinnerService,
  Option,
  ToasterService
} from '@amfam/ui-kit';

import * as fromDetailCommonSelector from '../../+state/selectors';
import { selectFilteredBillAccountNotification } from '../../+state/selectors';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'new-bill-account-detail-card',
  templateUrl: './new-bill-account-detail-card.component.html',
  styleUrls: ['./new-bill-account-detail-card.component.scss'],
  standalone: false
})
export class NewBillAccountDetailCardComponent implements OnInit, OnDestroy, OnChanges {
  @Input() billAccount: BillAccount;
  @Output() silentRegistrationFailureEvent = new EventEmitter();

  loading = true;
  onlineBillingText = ONLINE_BILLING;
  stopManagingAccountModal = 'stopManagingAccountModal';

  // Autopay Enrollment indicator
  matchingAutopayRule = null;

  // Pending payment details
  showPayBillButton = false;

  makePaymentBtnContent: string;
  payBillContent: string;
  setupAutoPayBtnContent: string;
  editPaymentContent: string;

  billAccountDisclaimer: string;
  status: BillAccountCardStatus;
  metaData: BillAccountCardMetadata;
  public thirdPartyInterest = [];
  public editLenderBtnName: string;
  public editLenderBtnContent: string;
  public viewPaymentsContent: string;
  public updateCardBtnContent: string;
  public editAutopayBtnContent: string;
  public autopayDayBeforeContent: string;
  public futurePayments;
  public onetimePaymentsLoading: boolean;
  public unAssociatedAccount: PolicyList;
  public matchedBillAccountNotificationState: BillingNotificationsState;
  public isBam: boolean;
  paymentMethodAddEditConfig$: BehaviorSubject<PaymentMethodAddEditConfig> = new BehaviorSubject(
    null
  );
  paymentMethodOptions: Option[];
  addPaymentMethodType: string;
  isContactBillingToggleEnabled = this.featureFlagService.isEnabled('contact_billing');

  public isAdminApp = this.applicationService.isApp(Applications.MYACCOUNT_ADMIN);
  editReminder = false;
  billReminderForm: UntypedFormGroup;
  billingReminderFrequencies = [];
  email$: Observable<string>;
  primaryButtonName = 'Save';
  tertiaryButtonName = 'Cancel';
  updateCorrelationId = this.utilService.generateId();
  billAccountNotification$ = this.store.select(
    selectFilteredBillAccountNotification(this.updateCorrelationId)
  );
  showSpinner = false;

  private policiesWithTPI: Policy[] = [];
  private isHSPolicy = false;
  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private featureFlagService: FeatureFlagService,
    public roleService: ImpersonateRolesService,
    public spinner: LoadingSpinnerService,
    private store: Store,
    private transmuteService: BillAccountTransmuteService,
    private utilService: UtilService,
    private copyService: CopyService,
    private applicationService: ApplicationService,
    private autoPayUtil: AutoPayUtilService,
    private router: Router,
    private analyticsFacade: AnalyticsFacade,
    private overviewBillingUtil: OverviewBillingUtilService,
    private route: ActivatedRoute,
    private dockingService: DockingBarService,
    private modalService: DsModalService,
    private formBuilder: UntypedFormBuilder,
    private toasterService: ToasterService
  ) {}

  ngOnInit() {
    this.editLenderBtnName = this.copyService.getCopy(
      'policy.thirdPartyInterest.editLender.buttonName'
    );
    this.editLenderBtnContent = this.copyService.getCopy('billing.editLender');
    this.viewPaymentsContent = this.copyService.getCopy('billing.viewPayments');
    this.updateCardBtnContent = this.copyService.getCopy('billing.updateCard');
    this.editAutopayBtnContent = this.copyService.getCopy('billing.editAutopay');
    this.editPaymentContent = this.copyService.getCopy('billing.editPayment');

    this.billAccountDisclaimer = this.copyService.getCopy(
      this.billAccount?.policyList?.length > 1
        ? 'policy.thirdPartyInterest.billing.disclaimerForCombinedPolicy'
        : 'policy.thirdPartyInterest.billing.disclaimer',
      {
        editLender: true
      }
    );
    this.makePaymentBtnContent = this.copyService.getCopy('billing.makePayment');
    this.payBillContent = this.copyService.getCopy('billing.payBill');
    this.setupAutoPayBtnContent = this.copyService.getCopy('billing.setUpAutoPay');

    const billAccountNumber = _get(this.billAccount, 'billAccountNumber');
    this.unAssociatedAccount = this.billAccount.policyList;

    this.store
      .select(fromDetailCommonSelector.selectCombinedAutoPayState(billAccountNumber))
      .pipe(takeUntil(this.stop$))
      .subscribe(state => {
        // Determine if we should show the pay bill button
        this.showPayBillButton = this.showPayBillButtonInit();

        this.policiesWithTPI = state.policiesWithTPI;

        // used to show Edit lender button
        this.determineTPI();
      });

    this.store
      .select(paymentHistoryQuery.selectLoading)
      .pipe(takeUntil(this.stop$))
      .subscribe(onetimePaymentsLoading => {
        this.onetimePaymentsLoading = onetimePaymentsLoading;
      });

    this.store
      .select(BillAccountsSelectors.selectIsSelectedBillPolicyHomesite)
      .pipe(takeUntil(this.stop$))
      .subscribe(isHSPolicy => {
        this.isHSPolicy = isHSPolicy;
      });

    this.email$ = this.store.pipe(
      select(userQuery.selectMyAccountEmail),
      map(email => _get(email, 'emailAddress', ''))
    );

    this.route.params
      .pipe(
        takeUntil(this.stop$),
        withLatestFrom(
          this.store.select(BillAccountsSelectors.selectBillAccountNotificationsEntities)
        )
      )
      .subscribe(([notificationsEntities]) => {
        this.matchedBillAccountNotificationState = notificationsEntities[billAccountNumber];
        this.dockingService.registerHeading('');
        if (this.isContactBillingToggleEnabled) {
          this.dockingService.registerAction(
            new Action('Contact Billing', () => {
              this.store.dispatch(
                fromRouterActions.Go({
                  path: [BillingPaymentPaths.BILLING_CONTACT, billAccountNumber]
                })
              );
            })
          );
        }
      });

    const isRegisteredHere =
      !this.billAccount.registeredElsewhere &&
      _get(this.billAccount, 'billingMethod') === ONLINE_BILLING;
    const unregisteredBam =
      _get(this.billAccount, 'billingPreferences.preferences[0].isModifiable') &&
      !!_get(this.billAccount, 'billingPreferences.preferences[0].preferenceId');
    // This is the logic API team said to use to determine bill account manager
    this.isBam = isRegisteredHere || unregisteredBam;

    this.requestHistory();
    this.getBillAccountCards();
  }

  openModal(): void {
    this.modalService.open(this.stopManagingAccountModal);
  }

  closeStopManagingAccountModal(): void {
    this.modalService.close(this.stopManagingAccountModal);
  }

  requestHistory() {
    this.store.dispatch(
      fromPaymentHistoryActions.GetPayments({ monthRange: { startMonth: 0, endMonth: -3 } })
    );
  }

  getBillAccountCards() {
    combineLatest([
      this.store.select(BillAccountsSelectors.selectBillAccounts),
      this.store.select(BillAccountsSelectors.selectAllBillAccountsNotifications),
      this.store.select(selectBillaccountsListNotEligibleForAutopay),
      this.store.select(ScheduledPaymentSelectors.selectScheduledPayments),
      this.store.select(ScheduledPaymentSelectors.selectSuccessfulPaymentsAllMethods),
      this.store.select(ScheduledPaymentSelectors.selectFailedPaymentsAllMethods),
      this.store.select(AutoPaySelectors.getAutomaticPayments),
      this.store.select(AutoPaySelectors.getAutoPayNotifications),
      this.store.select(PaymentMethodSelectors.selectPaymentMethods),
      this.store.select(paymentHistoryQuery.selectPayments)
    ])
      .pipe(
        filter(([billAccounts]) => billAccounts.length > 0),
        takeUntil(this.stop$),
        map(
          ([
            billAccounts,
            billAccountNotifications,
            billAccountsNotEligibleForAutoPay,
            scheduledPayments,
            successfulPayments,
            failedPayments,
            automaticPayments,
            automaticPaymentNotifications,
            paymentMethods,
            paymentHistory
          ]: [
            BillAccount[],
            BillingNotificationsState[],
            BillAccount[],
            ScheduledPayment[],
            ScheduledPayment[],
            ScheduledPayment[],
            AutomaticPayment[],
            EntityNotification[],
            PaymentMethod[],
            Payment[]
          ]) => {
            const automaticPaymentsToggle: AutomaticPayment[] = automaticPayments;
            const automaticPaymentNotificationsToggle: EntityNotification[] =
              automaticPaymentNotifications;

            const filteredPaymentMethods = paymentMethods?.filter(function (x) {
              return x !== undefined;
            });

            const paymentMethodsToggle: PaymentMethod[] = filteredPaymentMethods;

            const billAcc = billAccounts.find(
              billAccount => billAccount.billAccountNumber === this.billAccount.billAccountNumber
            );

            const futurePayments = paymentHistory.filter(
              payment =>
                payment.billAccounts.some(
                  account => account.billAccountNumber === this.billAccount.billAccountNumber
                ) &&
                (payment.paymentStatus === 'SCHEDULED' || payment.paymentStatus === 'AUTHORIZED')
            );

            const cardMetadata: BillAccountCardMetadata =
              this.overviewBillingUtil.getBillAccountCardMetadata(
                billAcc,
                billAccountNotifications,
                billAccountsNotEligibleForAutoPay,
                scheduledPayments,
                successfulPayments,
                failedPayments,
                automaticPaymentsToggle,
                automaticPaymentNotificationsToggle,
                paymentMethodsToggle,
                paymentHistory,
                false,
                false
              );
            const cardStatus: BillAccountCardStatus =
              this.overviewBillingUtil.getBillAccountCardStatus(cardMetadata);

            const billAccountCard: BillAccountCard = {
              icon: billAcc.icon,
              policyType: billAcc.policyTypeDisplayName,
              alert: billAcc.pastDue,
              status: cardStatus,
              metadata: cardMetadata
            };

            return { billAccountCard, futurePayments };
          }
        )
      )
      .subscribe(({ billAccountCard, futurePayments }) => {
        this.futurePayments = futurePayments;
        if (billAccountCard.status && billAccountCard.metadata) {
          // Augmenting statusText based on statusPillText and actionButton

          // when Autopay show Enrolled in autopay
          if (
            billAccountCard.status.statusPillText === 'Autopay' &&
            billAccountCard.status.actionButton === BillAccountActionButtons.EDIT_AUTOPAY
          ) {
            billAccountCard.status.statusText = 'Enrolled in autopay';
          }

          // When 1 Payment is processing show Payment Processing with date
          if (billAccountCard.status.statusText === '1 payments processing') {
            billAccountCard.status.statusText = 'Payment ' + billAccountCard.status.statusPillText;
          }

          // Balance due, show Autopay scheduled with date
          if (
            billAccountCard.status.statusText === '' &&
            billAccountCard.status.actionButton === BillAccountActionButtons.EDIT_AUTOPAY
          ) {
            const pillText = billAccountCard.status.statusPillText.split(' ');
            pillText.shift();
            const date = pillText.join(' ');
            billAccountCard.status.statusText = 'Autopay scheduled ' + date;
          }

          // When enrolled in AFT
          if (billAccountCard.metadata.hasAFT) {
            billAccountCard.status.statusText = this.copyService.getCopy('billing.AFTcontent');
          }

          // When Paid by Lender/ has TPI and has mindue
          if (
            billAccountCard.metadata.hasTPIPayer &&
            billAccountCard.metadata.hasMinimumAmountDue
          ) {
            billAccountCard.status.statusText =
              'Payment Scheduled ' +
              format(billAccountCard.metadata.minimumDueDate, 'MMMM d, yyyy');
            billAccountCard.status.statusIcon = BillAccountStatusIcons.CHECK;
          }

          // Single scheduled payment
          if (
            billAccountCard.metadata.hasSingleScheduledPayment &&
            billAccountCard.metadata.multipleScheduledPaymentsCount === 1
          ) {
            const isScheduledPayment =
              billAccountCard.metadata.scheduledPayments[0].paymentStatus ===
              PaymentStatus.SCHEDULED;
            billAccountCard.status.statusText = isScheduledPayment
              ? 'Payment Scheduled ' +
                format(_get(billAccountCard.metadata.scheduledPayments[0], 'receivedDate'), 'MMM D')
              : 'Payment Processing ' +
                format(
                  _get(billAccountCard.metadata.scheduledPayments[0], 'receivedDate'),
                  'MMM D'
                );
          }

          this.status = billAccountCard.status;
          this.metaData = billAccountCard.metadata;

          // billing reminders
          const daysBeforeDueDate = billAccountCard.metadata?.automaticPayments?.daysBeforeDueDate;
          if (daysBeforeDueDate === '0') {
            this.autopayDayBeforeContent = this.copyService.getCopy('billing.onTheDueDate');
          } else if (daysBeforeDueDate === '1') {
            this.autopayDayBeforeContent = this.copyService.getCopy('billing.oneDayBefore');
          } else {
            this.autopayDayBeforeContent = this.copyService.getCopy('billing.daysBefore', {
              days: daysBeforeDueDate
            });
          }

          this.loading = false;
        }
      });
  }

  ngOnChanges() {
    this.showPayBillButton = this.showPayBillButtonInit();
    this.determineTPI();
  }

  onEditLender() {
    this.analyticsFacade.trackButtonClick(BillingAnalytics.buttonBillingPaymentsEditLenderInfo);
    this.router.navigate(['/policies/changerequest'], {
      state: {
        changeRequestType: PolicyChangeRequestType.MORTGAGE_LENDER_DETAILS_CHANGE,
        selectedPolicyNumber: this.policiesWithTPI[0]?.policyNumber || ''
      }
    });
  }

  onUpdateCard() {
    this.router.navigate(['/billing/payment-method']);
  }

  editAutopay() {
    this.autoPayUtil.editAutoPay(this.billAccount.billAccountNumber, 'overview');
  }

  addAutopay() {
    this.autoPayUtil.addAutoPay(
      this.billAccount.billAccountNumber,
      `/billing/${this.billAccount.billAccountNumber}`,
      this.isHSPolicy
    );
    if (this.isHSPolicy) this.addPaymentMethod();
  }

  paymentMethodAddEditComplete(newPaymentAccountId: string): void {
    this.modalService.close('paymentMethodModal');

    const correlationId: string = this.utilService.generateId();
    this.spinner.start({
      blockActions: true
    });
    this.store.dispatch(PaymentMethodActions.getPaymentMethods({ correlationId }));
    this.store
      .select(PaymentMethodSelectors.selectPaymentMethodState)
      .pipe(
        filter(state => state.correlationId === correlationId),
        take(1)
      )
      .subscribe(state => {
        this.spinner.stop();
        if (!state.hasError) {
          const newPaymentMethodOption = state.entities[newPaymentAccountId];

          if (this.isHSPolicy) {
            this.modalService.closeAll();
            this.modalService.open('addPaymentMethodSuccessModal');
          }
        }
      });
  }

  addCheckingAccount(): void {
    this.openPaymentMethodModal({
      accountType: PaymentMethodAccountType.CHECKING,
      operation: PaymentMethodAddEditOperationType.ADD,
      paymentMethod: null
    });
  }

  addCreditDebit(): void {
    this.openPaymentMethodModal({
      accountType: PaymentMethodAccountType.CREDIT_DEBIT,
      operation: PaymentMethodAddEditOperationType.ADD,
      paymentMethod: null
    });
  }

  closeAddPaymentMethodSuccessModal(): void {
    this.store.dispatch(AutoPaySetupActions.setStep({ step: AutoPaySteps.REVIEW }));
    this.store.dispatch(
      fromRouterActions.Go({
        path: ['billing', 'auto-pay', 'review']
      })
    );
  }

  addPaymentMethod() {
    this.modalService.closeAll();
    this.modalService.open('choosePaymentMethod');
  }

  view() {
    this.router.navigate(['/billing'], {
      state: {
        futurePayments: false,
        billAccNumber: this.billAccount.billAccountNumber
      }
    });
  }

  editPayment(paymentId) {
    this.store.dispatch(
      OneTimePayActions.routeToReviewForEdit({
        paymentId
      })
    );
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  public registerOrPay() {
    this.store.dispatch(
      pendingPaymentActions.AddPendingPaymentAction({
        billAccountNumber: this.billAccount.billAccountNumber
      })
    );

    if (_get(this.billAccount, 'billingMethod') !== ONLINE_BILLING) {
      let routeToMakeAPayment: string;
      // ONE TIME PAYMENT ENTRYPOINT
      if (this.featureFlagService.isEnabled('simplified_payment')) {
        routeToMakeAPayment = BillingPaymentPaths.ADD_ONE_TIME_PAYMENT;
      } else {
        this.store
          .select(pendingPaymentQuery.selectBillAccountsEligibleForOneTimePay)
          .pipe(take(1))
          .subscribe(billAccounts => {
            if (billAccounts.length === 1) {
              routeToMakeAPayment = BillingPaymentPaths.REVIEW_ONE_TIME_PAYMENT;
            } else {
              routeToMakeAPayment = BillingPaymentPaths.ADD_ONE_TIME_PAYMENT;
            }
          });
      }
      // If the account isn't registered and pcm is enabled do silent registration.

      this.registerAndRoute([routeToMakeAPayment]);
    } else {
      // If the account is registered navigate to payment flow.
      // ONE TIME PAYMENT ENTRYPOINT
      if (this.featureFlagService.isEnabled('simplified_payment')) {
        this.store.dispatch(
          fromRouterActions.Go({
            path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT]
          })
        );
      } else {
        this.store
          .select(pendingPaymentQuery.selectBillAccountsEligibleForOneTimePay)
          .pipe(take(1))
          .subscribe(billAccounts => {
            if (billAccounts.length === 1) {
              this.store.dispatch(
                fromRouterActions.Go({
                  path: [BillingPaymentPaths.REVIEW_ONE_TIME_PAYMENT]
                })
              );
            } else {
              this.store.dispatch(
                fromRouterActions.Go({
                  path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT]
                })
              );
            }
          });
      }
    }
  }

  editReminderFrequency() {
    this.editReminder = true;
    this.billingReminderFrequencies = [7, 6, 5, 4, 3];
    this.setForm();
  }

  setForm() {
    const currentReminderFrequency = _get(this.billAccount, 'billingPreferences.dueDateReminder');
    this.billReminderForm = this.formBuilder.group({
      billingReminder: [currentReminderFrequency, Validators.compose([Validators.required])]
    });
  }

  saveReminderFrequency() {
    this.showSpinner = true;
    this.editReminder = false;
    // build payload
    const updateBillingPreferencePayload: UpdatePreferencesRequest = {
      billAccountNumber: this.billAccount.billAccountNumber,
      updatePreferenceCorrelationId: this.updateCorrelationId,
      associated: this.billAccount.associated,
      billingMethod: this.billAccount.billingMethod,
      payload: {
        billingPreference: {
          accountNickname: _get(this.billAccount, 'billingPreferences.accountNickName'),
          dueDateReminder: this.billReminderForm.controls.billingReminder.value,
          preferences: _get(this.billAccount, 'billingPreferences.preferences')
        }
      }
    };

    this.store.dispatch(
      BillAccountActions.billAccountUpdateBillingPreference(updateBillingPreferencePayload)
    );

    this.billAccountNotification$
      .pipe(filter(Boolean), take(1))
      .subscribe(matchingBillAccountNotification => {
        if (
          _get(
            matchingBillAccountNotification,
            'updatedBillingPreferences',
            matchingBillAccountNotification.updatedPreferences
          )
        ) {
          this.toasterService.pop('success', 'Billing alert updated');
        } else if (
          _get(
            matchingBillAccountNotification,
            'updateBillingPreferencesError',
            matchingBillAccountNotification.updatePreferencesError
          )
        ) {
          this.toasterService.pop('error', 'There was an error saving your billing alert');
        }

        this.store.dispatch(
          BillAccountActions.billAccountUpdateBillingPreferenceInit(
            this.billAccount.billAccountNumber
          )
        );
        this.showSpinner = false;
      });
  }

  cancel() {
    this.editReminder = false;
    this.billReminderForm.reset();
  }

  private openPaymentMethodModal(modalConfig: PaymentMethodAddEditConfig) {
    this.paymentMethodAddEditConfig$.next(modalConfig);
    this.modalService.open('paymentMethodModal', true);
  }

  private determineTPI() {
    this.thirdPartyInterest =
      this.policiesWithTPI
        ?.find(
          (policy: Policy) => policy?.billingAccountNumber === this.billAccount?.billAccountNumber
        )
        ?.policyRoles?.filter(el => el.roleType === 'BillTo')
        ?.slice(0, 3) || [];
  }

  // Takes one parameter, the path to route to after silent registration
  private registerAndRoute(path: string[]): void {
    this.spinner.start({
      blockActions: true
    });
    const correlationId = this.utilService.generateId();
    const silentRegistrationPayload: SilentRegistrationPayload[] =
      this.transmuteService.buildSilentRegistrationPayload(
        [this.billAccount.billAccountNumber],
        correlationId
      );
    this.store.dispatch(
      BillAccountActions.billAccountSilentRegistration(silentRegistrationPayload)
    );
    this.store
      .select(BillAccountsSelectors.selectBillAccounts)
      .pipe(
        mergeMap(billAccounts => billAccounts),
        withLatestFrom(
          this.store.select(BillAccountsSelectors.selectBillAccountNotificationsEntities),
          this.store.select(pendingPaymentQuery.selectBillAccountsEligibleForOneTimePay)
        ),
        filter(
          ([billingAccount, matchedBillAccountNotificationState]) =>
            matchedBillAccountNotificationState[billingAccount.billAccountNumber].correlationId ===
              correlationId &&
            (!!billingAccount.registrationDate ||
              !!matchedBillAccountNotificationState[billingAccount.billAccountNumber]
                .updateRegistrationError)
        ),
        take(1)
      )
      .subscribe(([billAccount]) => {
        this.spinner.stop();
        // Route to make a payment section if registration was successful
        if (_get(billAccount, 'registrationDate')) {
          this.store.dispatch(
            fromRouterActions.Go({
              path: [path[0]]
            })
          );
        }
        this.silentRegistrationFailureEvent.emit();
      });
  }

  private showPayBillButtonInit(): boolean {
    /*
      Show payment button if:
      1. The account isn't setup for AFT AND
      2. The account isn't enrolled elsewhere AND
      3. The account isn't already enrolled in autopay AND
      4. The account balance is > 0 - from a discussion with the PO & JJC
      5. The account isn't readonly once pcm is enabled.
      6. TODO: The account doesn't have a scheduled payment >= minimumAmountDue
    */

    return (
      !_get(this, 'billAccount.readOnly') &&
      !this.matchingAutopayRule &&
      _get(this, 'billAccount.accountBalance') > 0
    );
  }
}
